import {Contenitore} from '../_models/Contenitore';
import {Evento} from '../_models/Evento';
import {Movimento} from '../_models/Movimento';
import {Ordine} from '../_models/Ordine';
import {Risorsa} from '../_models/Risorsa';
import {Anagrafica} from '../_models/Anagrafica';
import {ControlloQualita} from '../_models/ControlloQualita';
import {StatoEnum} from '../_models/generated/stato-enum';

export interface StructuredNotificationMessage {
  message: string;
  details?: string[];
  route?: string;
}

export type NotificationAction = 'created' | 'updated' | 'deleted' | 'emptied';
export type NotificationEntityType = 'contenitore' | 'evento' | 'movimento' | 'ordine' | 'risorsa' | 'anagrafica' | 'controllo';

type NotificationEntity = Contenitore | Evento | Movimento | Ordine | Risorsa | Anagrafica | ControlloQualita;
type EntityLike = Record<string, unknown>;

export function buildEntityNotification(
  action: NotificationAction,
  entityType: NotificationEntityType,
  entity: NotificationEntity
): StructuredNotificationMessage {
  const source = entity as unknown as EntityLike;

  switch (entityType) {
    case 'contenitore':
      return {
        message: buildContenitoreMessage(action, source)
      };
    case 'evento':
      return {
        message: buildEventoMessage(action, source),
        route: action !== 'deleted' ? buildEventoRoute(source) : undefined
      };
    case 'movimento':
      return {
        message: buildMovimentoMessage(action, source),
        route: action !== 'deleted' ? buildMovimentoRoute(source) : undefined
      };
    case 'ordine':
      return {
        message: buildOrdineMessage(action, source)
      };
    case 'risorsa':
      return {
        message: buildRisorsaMessage(action, source)
      };
    case 'anagrafica':
      return {
        message: buildAnagraficaMessage(action, source)
      };
    case 'controllo':
      return {
        message: buildControlloMessage(action, source),
        route: action !== 'deleted' ? buildControlloRoute(source) : undefined
      };
  }
}

function buildAnagraficaMessage(action: NotificationAction, entity: EntityLike): string {
  const code = getString(entity, 'codice') ?? getString(entity, 'ragioneSociale');
  return buildMessage('Anagrafica', code, getActionText(action, true), entity);
}

function buildRisorsaMessage(action: NotificationAction, entity: EntityLike): string {
  const code = getString(entity, 'codice');
  return buildMessage('Risorsa', code, getActionText(action, true), entity);
}

function buildMovimentoMessage(action: NotificationAction, entity: EntityLike): string {
  const code = getString(entity, 'codBox') ?? getEntityId(entity);
  return buildMessage('Movimento', code, getActionText(action, false), entity);
}

function buildOrdineMessage(action: NotificationAction, entity: EntityLike): string {
  const orderKey = getOrderKey(entity);
  const machineCode = getOrderMachineCode(entity);
  const actor = getActorLabel(entity);
  const parts = [
    'Ordine',
    orderKey,
    machineCode ? `(macchina ${machineCode})` : undefined,
    getActionText(action, false),
    actor ? `da ${actor}` : undefined
  ].filter((value): value is string => Boolean(value));

  return `${parts.join(' ')}.`;
}

function buildEventoMessage(action: NotificationAction, entity: EntityLike): string {
  const auditMessage = buildUserAuditEventoMessage(entity);
  if (auditMessage) {
    return auditMessage;
  }

  const code = getString(entity, 'codiceRisorsa') ?? getEntityId(entity);
  return buildMessage('Evento', code, getActionText(action, false), entity);
}

function buildUserAuditEventoMessage(entity: EntityLike): string | undefined {
  const state = getNumber(entity, 'statoRisorsa');
  const subject = getUserLabel(entity, 'subject');
  if (!subject || state === undefined) {
    return undefined;
  }

  const action = state === StatoEnum.REGISTER
    ? 'registrato'
    : state === StatoEnum.USER_DELETED
      ? 'eliminato'
      : state === StatoEnum.PASSWORD_CHANGED
        ? 'ha cambiato la password'
        : undefined;
  if (!action) {
    return undefined;
  }

  const actor = getUserLabel(entity, 'actor') ?? getActorLabel(entity) ?? 'Sistema';
  return `Utente ${subject} ${action} da ${actor}.`;
}

function buildControlloMessage(action: NotificationAction, entity: EntityLike): string {
  const scheda = getNumber(entity, 'idSchedaControllo');
  const riga = getNumber(entity, 'idRigaCicloControllo');
  const controlloId = getNumber(entity, 'controlloId');
  const code = scheda !== undefined && riga !== undefined
    ? `${scheda}.${riga}${controlloId !== undefined ? ` #${controlloId}` : ''}`
    : getEntityId(entity);
  return buildMessage('Controllo', code, getActionText(action, false), entity);
}

function buildEventoRoute(entity: EntityLike): string | undefined {
  const id = getNumber(entity, 'id');
  return id !== undefined ? `/eventi/detail/${id}` : undefined;
}

function buildControlloRoute(entity: EntityLike): string | undefined {
  const idEvento = getNumber(entity, 'idEvento');
  return idEvento !== undefined ? `/eventi/detail/${idEvento}` : undefined;
}

function buildMovimentoRoute(entity: EntityLike): string | undefined {
  const id = getNumber(entity, 'id');
  if (id !== undefined) {
    return `/movimenti?movimentoId=${id}`;
  }

  const idEvento = getNumber(entity, 'idEvento');
  return idEvento !== undefined ? `/eventi/detail/${idEvento}` : undefined;
}

function buildContenitoreMessage(action: NotificationAction, entity: EntityLike): string {
  const code = getString(entity, 'codice') ?? getString(entity, 'codBox');
  return buildMessage('Contenitore', code, getActionText(action, false), entity);
}

function getActionText(action: NotificationAction, feminine: boolean): string {
  switch (action) {
    case 'created':
      return feminine ? 'creata' : 'creato';
    case 'updated':
      return feminine ? 'aggiornata' : 'aggiornato';
    case 'deleted':
      return feminine ? 'eliminata' : 'eliminato';
    case 'emptied':
      return feminine ? 'svuotata' : 'svuotato';
  }
}

function buildMessage(entityLabel: string, code: string | undefined, actionText: string, entity: EntityLike): string {
  const actor = getActorLabel(entity);
  const parts = [
    entityLabel,
    code,
    actionText,
    actor ? `da ${actor}` : undefined
  ].filter((value): value is string => Boolean(value));

  return `${parts.join(' ')}.`;
}

function getActorLabel(entity: EntityLike): string | undefined {
  return getString(entity, 'executedBy');
}

function getUserLabel(entity: EntityLike, prefix: 'actor' | 'subject'): string | undefined {
  return getString(entity, `${prefix}UserName`) ??
    getString(entity, `${prefix}DisplayName`) ??
    (getNumber(entity, `${prefix}UserId`) !== undefined
      ? `#${getNumber(entity, `${prefix}UserId`)}`
      : undefined);
}

function getOrderKey(entity: EntityLike): string | undefined {
  const rifOdP = getString(entity, 'rifOdP');
  if (rifOdP) {
    return rifOdP;
  }

  const idDocumento = getNumber(entity, 'idDocumento');
  const idRiga = getNumber(entity, 'idRiga');
  const idRigaLavorazione = getNumber(entity, 'idRigaLavorazione');
  if (idDocumento !== undefined && idRiga !== undefined && idRigaLavorazione !== undefined) {
    return `${idDocumento}.${idRiga}.${idRigaLavorazione}`;
  }

  return undefined;
}

function getOrderMachineCode(entity: EntityLike): string | undefined {
  const risorsa = getObject(entity, 'risorsa');
  const nestedCode = risorsa ? getString(risorsa, 'codice') : undefined;
  if (nestedCode) {
    return nestedCode;
  }

  return getString(entity, 'codiceRisorsa') ?? getString(entity, 'risorsa');
}

function getEntityId(entity: EntityLike): string | undefined {
  const id = getNumber(entity, 'id');
  return id !== undefined ? `#${id}` : undefined;
}

function getObject(entity: EntityLike, key: string): EntityLike | undefined {
  const value = entity[key];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }

  return value as EntityLike;
}

function getString(entity: EntityLike, key: string): string | undefined {
  const value = entity[key];
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

function getNumber(entity: EntityLike, key: string): number | undefined {
  const value = entity[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
